import type { ISavedPaymentMethod } from '@order-up/models';
import { createApiClient } from './apiClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('SavedCardsService');
const apiClient = createApiClient('/api/v1');

/**
 * The masked saved-card view the customer endpoints return — a subset of
 * `ISavedPaymentMethod` (no PAN, no doc internals). `instrument` is the neutral
 * `SavedTokenInstrument` carrying the provider + routing ref + display fields.
 */
export interface SavedCardView {
  readonly methodId: string;
  readonly instrument: ISavedPaymentMethod['instrument'];
  readonly isDefault: boolean;
}

/**
 * List the signed-in customer's saved cards at one restaurant. Saved cards are
 * merchant-scoped, so the (tenantDocId, restaurantDocId) pair is required.
 * The endpoint is phone-gated server-side.
 */
const listSavedCards = async (
  tenantDocId: string,
  locationDocId: string,
): Promise<SavedCardView[]> => {
  const response = (await apiClient.get('/customers/me/saved-cards', {
    params: { tenantDocId, restaurantDocId: locationDocId },
  })) as { data?: { savedCards?: SavedCardView[] } };
  return response.data?.savedCards ?? [];
};

/**
 * Remove a saved card. The server drops the customer index doc AND purges the
 * underlying record from the provider vault.
 */
const deleteSavedCard = async (
  tenantDocId: string,
  locationDocId: string,
  methodId: string,
): Promise<void> => {
  logger.debug('Deleting saved card', { tenantDocId, restaurantDocId: locationDocId, methodId });
  await apiClient.delete(`/customers/me/saved-cards/${methodId}`, {
    data: { tenantDocId, restaurantDocId: locationDocId },
  });
};

export const savedCardsService = {
  listSavedCards,
  deleteSavedCard,
};
